<template>
  <Card v-if="organization" :title="organization.name">
    <OrganizationForm v-model="organization"
                      :is-syndicate-organization="organization.type === OrganizationType.RegularSyndicate"
                      @submit="handleSubmit"
                      @on-image-change="onImageChange"/>
  </Card>

  <MemberTable v-if="organization && !memberStore.hasRole(Role.RegularSyndicateCoOwner)"
               :card-link-path="addMemberPath"
               :is-loading="organizationIsLoading"
               :members="organization.members"
               :organization-id="organization.id.toString()"/>

  <ContactTable v-if="organization && organization.type === OrganizationType.Corporate"
                :card-link-path="addContactPath"
                :contacts="organization.contacts"
                :is-loading="organizationIsLoading"
                :organization-id="organization.id.toString()"/>
</template>
<script lang="ts" setup>
import {onMounted, ref} from "vue"
import {useI18n} from "vue3-i18n"
import {useOrganizationService} from "@/inversify.config"
import {notifyError, notifySuccess} from "@/notify"
import {getAuthenticatedMemberOrganizationRoute} from "@/router/helpers"
import {OrganizationType, Role} from "@/types"
import {IFullOrganization} from "@/types/entities/organization";
import Card from "@/components/layout/Card.vue"
import MemberTable from "@/components/tables/MemberTable.vue"
import ContactTable from "@/components/tables/ContactTable.vue"
import {useMemberStore} from "@/stores/memberStore";
import {getFileFromUrl} from "@/helpers";
import OrganizationForm from "@/components/forms/OrganizationForm.vue";
import {IEditOwnOrganizationRequest} from "@/types/requests/editOwnOrganizationRequest";

// eslint-disable-next-line
const props = defineProps<{
  organizationId: string
}>()

const {t, getLocale} = useI18n()
const memberStore = useMemberStore()
const organizationService = useOrganizationService()

const organizationLogo = ref<File>()
const organization = ref<IFullOrganization>()
const organizationIsLoading = ref(false)

const addMemberPath = {
  name: `${getAuthenticatedMemberOrganizationRoute()}.children.addMember`,
  params: {organizationId: props.organizationId}
};
const addContactPath = {
  name: `${getAuthenticatedMemberOrganizationRoute()}.children.addContact`,
  params: {organizationId: props.organizationId}
};

onMounted(() => {
  loadOrganization()
})

async function loadOrganization() {
  organizationIsLoading.value = true

  let fullOrganization = await organizationService.getMyOrganization(getLocale())
  if (fullOrganization) {
    organization.value = fullOrganization
    if (fullOrganization.logo)
      organizationLogo.value = await getFileFromUrl(fullOrganization.logo, fullOrganization.logo)
  }
  organizationIsLoading.value = false
}

async function onImageChange(file: File) {
  organizationLogo.value = file
}

async function handleSubmit(organization: IFullOrganization) {
  let request: IEditOwnOrganizationRequest = {
    type: organization.type,
    logo: organizationLogo.value,
    email: organization.email,
    phoneNumber: organization.phoneNumber,
    phoneExtension: organization.phoneExtension,
    apartment: organization.apartment,
    street: organization.street,
    city: organization.city,
    zipCode: organization.zipCode,
    enterpriseNumber: organization.enterpriseNumber,
    website: organization.website,
    numberOfUnits: organization.numberOfUnits ?? 0,
    yearsOfExperience: organization.yearsOfExperience,
    activityAreas: organization.activityAreas,
    descriptionFr: organization.descriptionFr,
    descriptionEn: organization.descriptionEn
  };
  let succeededOrNotResponse = await organizationService.editMyOrganization(request)
  if (succeededOrNotResponse && succeededOrNotResponse.succeeded) {
    notifySuccess(t('validation.organization.update.success'))
  } else {
    let errorMessages = succeededOrNotResponse.getErrorMessages(
        'validation.organization.update', 'validation.organization.update.errorOccured');
    notifyError(errorMessages[0])
  }
}
</script>